import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useSearch, useLocation, Link } from "wouter";
import {
  useListIssues,
  useListStatuses,
  useListCategories,
  useUpvoteIssue,
  useGetIssuePublicComments,
  Status,
  Category,
  Issue,
  UpvoteResult,
  ApiError,
} from "@workspace/api-client-react";
import { useQueryClient, QueryClient } from "@tanstack/react-query";
import Layout from "@/components/Layout";
import PageHead from "@/components/PageHead";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Search, Users, ThumbsUp, Shield, Wrench } from "lucide-react";
import { format } from "date-fns";
import IssueCard from "@/components/IssueCard";
import { statusPillStyle, statusLegendText } from "@/lib/status-presentation";
import { issuePageUrl } from "@/lib/issue-url";
import {
  type BoardBucket,
  countBuckets,
  filterBoardIssues,
  isResolvedStatus,
} from "@/lib/board-filters";

function isValidEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

const ISSUES_QUERY_KEY = ["/api/issues"];

function applyOptimisticCount(
  queryClient: QueryClient,
  issueId: number,
  delta: number,
): Issue[] | undefined {
  const prev = queryClient.getQueryData<Issue[]>(ISSUES_QUERY_KEY);
  if (prev) {
    queryClient.setQueryData<Issue[]>(
      ISSUES_QUERY_KEY,
      prev.map((i) =>
        i.id === issueId
          ? { ...i, affectedCount: (i.affectedCount ?? 0) + delta }
          : i,
      ),
    );
  }
  return prev;
}

interface UpvoteModalProps {
  issue: Issue;
  open: boolean;
  onClose: () => void;
  onSettled: () => void;
}

function UpvoteModal({ issue, open, onClose, onSettled }: UpvoteModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [comment, setComment] = useState("");
  const [emailError, setEmailError] = useState("");
  const [serverError, setServerError] = useState("");
  const [alreadyVoted, setAlreadyVoted] = useState(false);
  const upvote = useUpvoteIssue();

  const handleClose = () => {
    setEmail("");
    setComment("");
    setEmailError("");
    setServerError("");
    setAlreadyVoted(false);
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError("");
    setServerError("");
    setAlreadyVoted(false);

    if (!email.trim()) {
      setEmailError("Email is required.");
      return;
    }
    if (!isValidEmail(email)) {
      setEmailError("Please enter a valid email address.");
      return;
    }

    const snapshot = applyOptimisticCount(queryClient, issue.id, +1);

    upvote.mutate(
      { id: issue.id, data: { email: email.trim(), comment: comment.trim() || undefined } },
      {
        onSuccess: (data: UpvoteResult) => {
          if (data.newAffectedCount != null) {
            const current = queryClient.getQueryData<Issue[]>(ISSUES_QUERY_KEY);
            if (current) {
              queryClient.setQueryData<Issue[]>(
                ISSUES_QUERY_KEY,
                current.map((i) =>
                  i.id === issue.id
                    ? { ...i, affectedCount: data.newAffectedCount! }
                    : i,
                ),
              );
            }
          }
          onSettled();
          toast({ title: "You're counted", description: "You've marked this issue as affecting you." });
          handleClose();
        },
        onError: (err: unknown) => {
          if (snapshot !== undefined) {
            queryClient.setQueryData(ISSUES_QUERY_KEY, snapshot);
          }
          const status = err instanceof ApiError ? err.status : undefined;
          const serverMessage =
            err instanceof ApiError
              ? (err.data as { error?: string } | null)?.error
              : undefined;
          const msg =
            serverMessage ??
            (err instanceof Error ? err.message : undefined) ??
            "Something went wrong.";
          if (status === 409) {
            setAlreadyVoted(true);
          } else {
            setServerError(msg);
          }
        },
        onSettled: () => {
          queryClient.invalidateQueries({ queryKey: ISSUES_QUERY_KEY });
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="sm:max-w-[420px]" data-testid="dialog-upvote">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ThumbsUp className="w-5 h-5 text-[var(--dna-indigo-700)]" />
            This affects me
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[var(--dna-slate-600)] -mt-1">
          Add yourself to the count for <span className="font-medium text-[var(--dna-ink)]">{issue.title}</span>.
          The count helps DNA staff see how widespread a problem is.
        </p>

        {alreadyVoted ? (
          <div className="rounded-lg bg-[var(--dna-warning-bg)] border border-[var(--dna-amber-300)] px-4 py-3 text-sm text-[var(--dna-amber-700)]" data-testid="text-already-upvoted">
            You've already marked this issue as affecting you with that email address. You're counted once.
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <Label htmlFor="upvote-email">
              Email <span className="text-destructive">*</span>
            </Label>
            <Input
              id="upvote-email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setEmailError(""); }}
              data-testid="input-upvote-email"
              autoComplete="email"
              aria-describedby="upvote-email-help"
            />
            <p id="upvote-email-help" className="text-xs text-[var(--dna-slate-500)]" data-testid="text-upvote-email-help">
              We use your email to prevent duplicate counts. It is not shown publicly, and this does not sign you up for emails.
            </p>
            {emailError && (
              <p className="text-xs text-destructive" data-testid="text-email-error">{emailError}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="upvote-comment">
              Additional comments <span className="text-[var(--dna-slate-400)] text-xs font-normal">(optional)</span>
            </Label>
            <Textarea
              id="upvote-comment"
              placeholder="Describe how this issue affects you..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="min-h-[80px] resize-none"
              data-testid="input-upvote-comment"
            />
          </div>

          {serverError && (
            <p className="text-xs text-destructive" data-testid="text-server-error">{serverError}</p>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={handleClose} data-testid="button-upvote-cancel">
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[var(--dna-indigo-700)] hover:bg-[var(--dna-indigo-800)] text-white"
              disabled={upvote.isPending}
              data-testid="button-upvote-submit"
            >
              {upvote.isPending ? "Submitting…" : "This affects me"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function IssueBoardPage() {
  const { data: issues, isLoading: issuesLoading, isError: issuesFailed } = useListIssues();
  // Unscoped on purpose: staff can put a board row on a report-only status, and
  // the Open/Fixed split needs that status too.
  const { data: issueStatuses } = useListStatuses();
  const { data: categories } = useListCategories();
  const lastTriggerRef = useRef<HTMLElement | null>(null);
  const [query, setQuery] = useState("");
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [bucket, setBucket] = useState<BoardBucket>("open");

  const search = useSearch();
  const [, navigate] = useLocation();

  const urlIssueId = useMemo(() => {
    const params = new URLSearchParams(search);
    const raw = params.get("issue");
    if (!raw) return null;
    const parsed = parseInt(raw, 10);
    return isNaN(parsed) ? null : parsed;
  }, [search]);

  const statusById = new Map<number, Status>((issueStatuses ?? []).map((s) => [s.id, s]));
  const categoryById = new Map<number, Category>((categories ?? []).map((c) => [c.id, c]));
  const categoryNameOf = (issue: Issue) =>
    issue.categoryId != null ? categoryById.get(issue.categoryId)?.name : undefined;

  const selectedIssue = useMemo(() => {
    if (urlIssueId === null || !issues) return null;
    return issues.find((i) => i.id === urlIssueId) ?? null;
  }, [urlIssueId, issues]);

  const openIssue = (issue: Issue, trigger: HTMLElement) => {
    lastTriggerRef.current = trigger;
    const params = new URLSearchParams(search);
    params.set("issue", String(issue.id));
    navigate(`?${params.toString()}`);
  };

  const closeModal = useCallback(() => {
    const params = new URLSearchParams(search);
    params.delete("issue");
    const qs = params.toString();
    navigate(qs ? `?${qs}` : "?");
  }, [search, navigate]);

  const handleDialogChange = (open: boolean) => {
    if (!open) {
      closeModal();
    }
  };

  const prevSelectedIssueRef = useRef<Issue | null>(null);
  useEffect(() => {
    if (prevSelectedIssueRef.current !== null && selectedIssue === null) {
      window.setTimeout(() => {
        lastTriggerRef.current?.focus();
      }, 0);
    }
    prevSelectedIssueRef.current = selectedIssue;
  }, [selectedIssue]);

  // Statuses can load after issues, so the tab follows the resolved flag too,
  // not just the issue id.
  const selectedIssueIsResolved = selectedIssue
    ? isResolvedStatus(statusById.get(selectedIssue.statusId))
    : null;
  useEffect(() => {
    if (selectedIssueIsResolved === null) return;
    setBucket(selectedIssueIsResolved ? "fixed" : "open");
  }, [selectedIssue?.id, selectedIssueIsResolved]);

  const selectedStatus = selectedIssue ? statusById.get(selectedIssue.statusId) : undefined;
  const selectedCategoryName =
    selectedIssue && selectedIssue.categoryId != null
      ? categoryById.get(selectedIssue.categoryId)?.name
      : null;
  const referencedStatusIds = new Set((issues ?? []).map((i) => i.statusId));
  const visibleStatuses = (issueStatuses ?? []).filter(
    (s) => (s.isActive && s.appliesToIssues) || referencedStatusIds.has(s.id),
  );
  const visibleCategories = (categories ?? []).filter((c) => c.isActive);

  const [upvoteTarget, setUpvoteTarget] = useState<Issue | null>(null);

  const { data: selectedIssueComments } = useGetIssuePublicComments(
    selectedIssue?.id ?? 0,
    {
      query: {
        enabled: selectedIssue !== null,
        queryKey: [`/api/issues/${selectedIssue?.id ?? 0}/comments`],
      },
    },
  );

  const filterOpts = {
    query,
    categoryId,
    statusById,
    categoryNameOf,
  };
  const bucketCounts = countBuckets(issues ?? [], filterOpts);
  const visibleIssues = filterBoardIssues(issues ?? [], { ...filterOpts, bucket });

  const selectedPill = statusPillStyle(selectedStatus?.key);
  const publicUrl = ((import.meta.env.VITE_PUBLIC_URL as string | undefined) ?? "").replace(
    /\/$/,
    "",
  );

  return (
    <Layout>
      <PageHead
        title="MOTUS Bug Board — Track FMCSA MOTUS Bugs & Outages"
        description="Crowdsourced status board for the FMCSA's new MOTUS registration system. See known bugs, outages, and workarounds reported by carriers and compliance professionals."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "MOTUS Bug Board",
          alternateName: "Don Norman Associates MOTUS Bug Board",
          url: publicUrl || "/",
          description:
            "Independent crowdsourced bug board for the FMCSA MOTUS registration system, operated by Don Norman Associates.",
          publisher: {
            "@type": "Organization",
            name: "Don Norman Associates",
            url: "https://donnormanassociates.com",
            logo: publicUrl ? `${publicUrl}/dna-logo-2026.png` : "/dna-logo-2026.png",
          },
        }}
      />

      <section className="border-b border-[var(--dna-slate-200)] bg-[var(--dna-slate-50)]">
        <div className="mx-auto flex max-w-[var(--dna-maxw)] flex-col gap-4 px-4 py-10 md:px-10 md:py-12">
          <p className="dna-overline">MOTUS known issues · Tracked by DNA staff</p>
          <h1 className="dna-display-title" data-testid="text-hero-title">
            Is your MOTUS problem a known bug?
          </h1>
          {/*
            Two sentences an AI answer can lift whole, so an answer about MOTUS
            bugs names this board. Kept in step with HOMEPAGE_OPENING in
            artifacts/api-server/src/public-pages/content.ts and with the no-JS
            fallback in index.html.
          */}
          <p className="max-w-3xl text-[15px] leading-relaxed text-[var(--dna-slate-600)]" data-testid="text-hero-summary">
            MOTUS Bug Board (motusbugs.com) is a free public tracker for bugs and outages in MOTUS,
            the FMCSA's motor carrier registration system that launched on May 19, 2026. Carriers
            and compliance professionals report MOTUS problems here, and Don Norman Associates staff
            review each report, confirm real bugs, and flag known workarounds.
          </p>
          <label className="dna-search mt-2">
            <Search className="h-5 w-5 shrink-0 text-[var(--dna-slate-500)]" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={'Describe the error, e.g. "account not recognized"'}
              aria-label="Search known issues"
              data-testid="input-issue-search"
              autoComplete="off"
            />
          </label>
          <p className="text-sm text-[var(--dna-slate-600)]">
            Already filed a report?{" "}
            <Link href="/my-reports" className="dna-link font-semibold" data-testid="link-check-status">
              Track my reports
            </Link>
            {" · "}
            {/* Server-rendered pages, so a full navigation, not a client route. */}
            <a href="/issues" className="dna-link font-semibold" data-testid="link-all-issues">
              Browse all issues
            </a>
            {" · "}
            <a href="/guides/motus-login-errors" className="dna-link font-semibold" data-testid="link-login-errors">
              Login errors and fixes
            </a>
          </p>
          <div className="flex flex-wrap gap-2" data-testid="filter-area-chips">
            <button
              type="button"
              className={categoryId === null ? "dna-chip dna-chip-active" : "dna-chip"}
              onClick={() => setCategoryId(null)}
              data-testid="chip-area-all"
            >
              All areas
            </button>
            {visibleCategories.map((c) => (
              <button
                key={c.id}
                type="button"
                className={categoryId === c.id ? "dna-chip dna-chip-active" : "dna-chip"}
                onClick={() => setCategoryId(c.id)}
                data-testid={`chip-area-${c.id}`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      <div className="bg-white">
      <div className="mx-auto grid max-w-[var(--dna-maxw)] grid-cols-1 gap-10 px-4 py-7 md:px-10 md:py-12 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="flex min-w-0 flex-col">
          <div className="flex items-center justify-between gap-4 border-b border-[var(--dna-slate-200)] pb-3">
            <div className="flex gap-6 text-sm font-medium" role="tablist" aria-label="Issue status">
              <button
                type="button"
                role="tab"
                aria-selected={bucket === "open"}
                className={
                  bucket === "open"
                    ? "mb-[-13px] border-b-2 border-[var(--dna-indigo-700)] pb-2.5 font-semibold text-[var(--dna-ink)]"
                    : "text-[var(--dna-slate-600)]"
                }
                onClick={() => setBucket("open")}
                data-testid="tab-open"
              >
                Open{" "}
                <span className="dna-mono text-[var(--dna-slate-500)]">{bucketCounts.open}</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={bucket === "fixed"}
                className={
                  bucket === "fixed"
                    ? "mb-[-13px] border-b-2 border-[var(--dna-indigo-700)] pb-2.5 font-semibold text-[var(--dna-ink)]"
                    : "text-[var(--dna-slate-600)]"
                }
                onClick={() => setBucket("fixed")}
                data-testid="tab-fixed"
              >
                Fixed{" "}
                <span className="dna-mono text-[var(--dna-slate-500)]">{bucketCounts.fixed}</span>
              </button>
            </div>
            <span className="hidden text-[13px] text-[var(--dna-slate-600)] sm:inline">
              Sort: <strong className="text-[var(--dna-ink)]">Most carriers affected</strong>
            </span>
          </div>

          {issuesLoading ? (
            <div className="space-y-3 pt-4">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : issuesFailed && !issues ? (
            <div className="py-16 text-center" role="alert" data-testid="text-issues-error">
              <h2 className="dna-h3 mb-2">We couldn't load the issue list</h2>
              <p className="text-sm text-[var(--dna-slate-500)]">
                Refresh the page to try again.
              </p>
            </div>
          ) : issues?.length === 0 ? (
            <div className="py-16 text-center">
              <h2 className="dna-h3 mb-2">No known issues</h2>
              <p className="text-sm text-[var(--dna-slate-500)]">
                Everything seems to be running smoothly, or issues haven't been reported yet.
              </p>
            </div>
          ) : visibleIssues.length === 0 && query.trim() === "" ? (
            <div className="py-16 text-center" data-testid="text-empty-bucket">
              <h2 className="dna-h3 mb-2">
                {bucket === "open" ? "No open issues" : "No fixed issues yet"}
                {categoryId !== null ? " in this area" : ""}
              </h2>
              <p className="text-sm text-[var(--dna-slate-500)]">
                {bucket === "open"
                  ? bucketCounts.fixed > 0
                    ? "Everything here is marked fixed. Check the Fixed tab, or report a bug if yours is still happening."
                    : "Report a bug if you don't see yours."
                  : "Open issues are on the Open tab."}
              </p>
            </div>
          ) : visibleIssues.length === 0 ? (
            <div className="py-16 text-center" data-testid="text-empty-filter">
              <h2 className="dna-h3 mb-2">No issues match</h2>
              <p className="text-sm text-[var(--dna-slate-500)]">
                Try another search or area, or report a bug if you don't see yours.
              </p>
            </div>
          ) : (
            <div>
              {visibleIssues.map((issue) => (
                <IssueCard
                  key={issue.id}
                  issue={issue}
                  status={statusById.get(issue.statusId)}
                  categoryName={categoryNameOf(issue)}
                  onOpen={openIssue}
                  onUpvote={setUpvoteTarget}
                />
              ))}
            </div>
          )}
        </div>

        <aside className="flex flex-col gap-5">
          <div className="dna-card flex flex-col gap-3">
            <h2 className="dna-h3">Don't see your problem?</h2>
            <p className="text-sm leading-relaxed text-[var(--dna-slate-600)]">
              Tell us what happened. DNA staff review every report.
            </p>
            <Link
              href="/report"
              className="dna-btn-primary dna-btn-primary-block"
              data-testid="link-submit-report"
            >
              Report a bug
            </Link>
          </div>
          <div className="dna-card flex flex-col gap-3.5">
            <p className="text-xs font-semibold uppercase tracking-[0.06em] text-[var(--dna-slate-600)]">
              What the statuses mean
            </p>
            {visibleStatuses.map((s) => {
              const pill = statusPillStyle(s.key);
              return (
                <div key={s.id} className="flex flex-col gap-1.5">
                  <span className="dna-status-pill self-start" style={pill}>
                    {s.label}
                  </span>
                  <span className="text-[13px] leading-snug text-[var(--dna-slate-600)]">
                    {statusLegendText(s.key)}
                  </span>
                </div>
              );
            })}
          </div>
          <p className="px-1 text-[13px] leading-relaxed text-[var(--dna-slate-600)]">
            Need a filing handled? DNA has handled federal trucking filings since 1955.{" "}
            <a href="tel:2022930563" className="dna-mono dna-link whitespace-nowrap text-[var(--dna-ink)]">
              202-293-0563
            </a>
          </p>
        </aside>
      </div>
      </div>

      <Dialog open={selectedIssue !== null} onOpenChange={handleDialogChange}>
        <DialogContent
          className="max-h-[85vh] max-w-2xl overflow-y-auto"
          data-testid="dialog-issue-detail"
        >
          {selectedIssue && (
            <>
              <DialogHeader>
                <div className="mb-2 flex flex-wrap items-center gap-1.5">
                  <Badge
                    variant="outline"
                    className="font-medium"
                  >
                    {selectedCategoryName ?? "Uncategorized"}
                  </Badge>
                  {selectedIssue.hasWorkaround && (
                    <Badge
                      title="A workaround is available for this issue"
                      className="flex items-center gap-1 border-transparent bg-[var(--dna-indigo-50)] text-[var(--dna-indigo-700)] text-[10px] font-semibold uppercase tracking-wide"
                      data-testid={`badge-workaround-detail-${selectedIssue.id}`}
                    >
                      <Wrench className="w-3 h-3" /> Workaround
                    </Badge>
                  )}
                  <span className="dna-status-pill" style={selectedPill}>
                    {selectedStatus?.label ?? "—"}
                  </span>
                </div>
                <DialogTitle
                  className="pr-8 text-xl leading-tight font-[family-name:var(--dna-font-display)]"
                  data-testid="text-dialog-issue-title"
                >
                  {selectedIssue.title}
                </DialogTitle>
              </DialogHeader>
              <p
                className="whitespace-pre-wrap break-words text-sm text-[var(--dna-slate-700)]"
                data-testid="text-dialog-issue-description"
              >
                {selectedIssue.description}
              </p>

              {selectedIssueComments && selectedIssueComments.length > 0 && (
                <div
                  className="mt-2 space-y-3"
                  data-testid="section-upvote-comments"
                >
                  <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--dna-slate-500)]">
                    <ThumbsUp className="w-3.5 h-3.5" />
                    Comments
                  </h3>
                  <ul className="space-y-2">
                    {selectedIssueComments.map((c, i) =>
                      c.kind === "staff" ? (
                        <li
                          key={i}
                          className="rounded-[var(--dna-radius-md)] border border-[var(--dna-indigo-200)] bg-[var(--dna-indigo-50)] px-3 py-2.5"
                          data-testid={`staff-comment-${i}`}
                        >
                          <div className="mb-1 flex items-center gap-1.5">
                            <Shield className="h-3.5 w-3.5 text-[var(--dna-indigo-700)]" />
                            <span className="text-[11px] font-semibold uppercase tracking-wide text-[var(--dna-indigo-700)]">
                              {c.authorName ?? "DNA Staff"}
                            </span>
                          </div>
                          <p className="whitespace-pre-wrap break-words text-sm leading-snug text-[var(--dna-slate-700)]">
                            {c.comment}
                          </p>
                          <p className="mt-1 text-[11px] text-[var(--dna-slate-400)]">
                            {format(new Date(c.createdAt), "MMM d, yyyy")}
                          </p>
                        </li>
                      ) : (
                        <li
                          key={i}
                          className="rounded-[var(--dna-radius-md)] border border-[var(--dna-slate-200)] bg-[var(--dna-slate-50)] px-3 py-2.5"
                          data-testid={`upvote-comment-${i}`}
                        >
                          <div className="mb-1 flex items-center gap-1.5">
                            <Users className="h-3.5 w-3.5 text-[var(--dna-slate-400)]" />
                            <span className="text-[11px] font-semibold uppercase tracking-wide text-[var(--dna-slate-400)]">
                              Carrier
                            </span>
                          </div>
                          <p className="break-words text-sm leading-snug text-[var(--dna-slate-700)]">
                            "{c.comment}"
                          </p>
                          <p className="mt-1 text-[11px] text-[var(--dna-slate-400)]">
                            {format(new Date(c.createdAt), "MMM d, yyyy")}
                          </p>
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--dna-slate-200)] pt-4 text-xs text-[var(--dna-slate-500)]">
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1">
                    <Users className="h-3.5 w-3.5" />
                    {selectedIssue.affectedCount || 0} affected
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex h-7 items-center gap-1.5 px-3 text-xs"
                    onClick={() => setUpvoteTarget(selectedIssue)}
                    data-testid={`button-upvote-detail-${selectedIssue.id}`}
                  >
                    <ThumbsUp className="h-3.5 w-3.5" />
                    This affects me
                  </Button>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {/*
                    The issue's own page, rendered by the API server, is what
                    people share and what search and AI answers cite. Plain
                    <a> on purpose: it isn't a route in this app.
                  */}
                  <a
                    href={issuePageUrl(selectedIssue)}
                    className="dna-link font-semibold"
                    data-testid={`link-issue-page-${selectedIssue.id}`}
                  >
                    Open this issue's page
                  </a>
                  <span>
                    Created {format(new Date(selectedIssue.createdAt), "MMM d, yyyy")}
                  </span>
                  <span>
                    Updated {format(new Date(selectedIssue.updatedAt), "MMM d, yyyy")}
                  </span>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {upvoteTarget && (
        <UpvoteModal
          issue={upvoteTarget}
          open={upvoteTarget !== null}
          onClose={() => setUpvoteTarget(null)}
          onSettled={() => {}}
        />
      )}
    </Layout>
  );
}
